
import { createContext, useContext, useEffect, useState } from "react";
import { User } from "firebase/auth";
import { auth, onAuthStateChanged, signInWithEmail, signUpWithEmail, signInWithGoogle, signOut } from "@/lib/firebase";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import analytics from "@/lib/analytics";
import { AnalyticsEvents, AnalyticsParams } from "@/lib/analytics";
import { captureFirebaseError, setSentryUser, clearSentryUser } from "./useSentryErrorCapture";
import { FirebaseError } from "firebase/app";
import { useLoadingMetrics, LoadingPhase } from "./useLoadingMetrics";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => {},
  signUp: async () => {},
  loginWithGoogle: async () => {},
  logout: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { startTransaction, finishTransaction, logLoadingEvent } = useLoadingMetrics();

  useEffect(() => {
    // Start auth initialization tracking
    const authTransaction = startTransaction(LoadingPhase.AUTH_INITIALIZATION, {
      provider: 'firebase',
      persistenceEnabled: true
    });
    
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      
      // Log authentication state change
      logLoadingEvent(
        currentUser ? 'User authenticated' : 'User not authenticated',
        {
          userId: currentUser?.uid,
          email: currentUser?.email,
          provider: currentUser?.providerData[0]?.providerId,
          isAnonymous: currentUser?.isAnonymous
        }
      );
      
      // Update Sentry user context
      if (currentUser) {
        setSentryUser({
          id: currentUser.uid,
          email: currentUser.email || undefined,
          username: currentUser.displayName || undefined
        });
      } else {
        clearSentryUser();
      }
      
      // Finish auth initialization tracking
      finishTransaction(LoadingPhase.AUTH_INITIALIZATION, 'ok', {
        authenticated: !!currentUser,
        userId: currentUser?.uid,
        provider: currentUser?.providerData[0]?.providerId
      });
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      await signInWithEmail(email, password);
      
      // Track successful login
      analytics.trackEvent(AnalyticsEvents.LOGIN, {
        [AnalyticsParams.AUTH_METHOD]: 'email',
        [AnalyticsParams.SUCCESS]: true
      });
      
      toast({
        title: "Welcome back!",
        description: "You have successfully logged in.",
      });
      navigate("/connections");
    } catch (error) {
      console.error("Login error:", error);
      
      // Capture Firebase error in Sentry
      if (error instanceof FirebaseError) {
        captureFirebaseError(error, { authMethod: 'email', action: 'login' });
      }
      
      // Track failed login
      analytics.trackEvent(AnalyticsEvents.LOGIN, {
        [AnalyticsParams.AUTH_METHOD]: 'email',
        [AnalyticsParams.SUCCESS]: false,
        [AnalyticsParams.ERROR_MESSAGE]: error instanceof Error ? error.message : 'Unknown error'
      });
      
      toast({
        title: "Login failed",
        description: error instanceof Error ? error.message : "Please check your credentials and try again.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true);
      await signUpWithEmail(email, password);
      
      // Track successful signup
      analytics.trackEvent(AnalyticsEvents.SIGNUP, {
        [AnalyticsParams.AUTH_METHOD]: 'email',
        [AnalyticsParams.SUCCESS]: true
      });
      
      toast({
        title: "Account created!",
        description: "Your account has been successfully created.",
      });
      navigate("/connections");
    } catch (error) {
      console.error("Signup error:", error);
      
      // Capture Firebase error in Sentry
      if (error instanceof FirebaseError) {
        captureFirebaseError(error, { authMethod: 'email', action: 'signup' });
      }
      
      // Track failed signup
      analytics.trackEvent(AnalyticsEvents.SIGNUP, {
        [AnalyticsParams.AUTH_METHOD]: 'email',
        [AnalyticsParams.SUCCESS]: false,
        [AnalyticsParams.ERROR_MESSAGE]: error instanceof Error ? error.message : 'Unknown error'
      });
      
      toast({
        title: "Sign up failed",
        description: error instanceof Error ? error.message : "Please try again with different credentials.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      await signInWithGoogle();
      
      // Track successful Google login
      analytics.trackEvent(AnalyticsEvents.LOGIN_WITH_GOOGLE, {
        [AnalyticsParams.AUTH_METHOD]: 'google',
        [AnalyticsParams.SUCCESS]: true
      });
      
      toast({
        title: "Welcome!",
        description: "You have successfully logged in with Google.",
      });
      navigate("/connections");
    } catch (error) {
      console.error("Google login error:", error);
      
      // Capture Firebase error in Sentry
      if (error instanceof FirebaseError) {
        captureFirebaseError(error, { authMethod: 'google', action: 'login' });
      }
      
      // Track failed Google login
      analytics.trackEvent(AnalyticsEvents.LOGIN_WITH_GOOGLE, {
        [AnalyticsParams.AUTH_METHOD]: 'google',
        [AnalyticsParams.SUCCESS]: false,
        [AnalyticsParams.ERROR_MESSAGE]: error instanceof Error ? error.message : 'Unknown error'
      });
      
      toast({
        title: "Login failed",
        description: error instanceof Error ? error.message : "Failed to login with Google. Please try again.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut();
      
      // Track logout
      analytics.trackEvent(AnalyticsEvents.LOGOUT);
      
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      
      // Capture Firebase error in Sentry
      if (error instanceof FirebaseError) {
        captureFirebaseError(error, { action: 'logout' });
      }
      
      toast({
        title: "Logout failed",
        description: "Failed to log out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signUp,
        loginWithGoogle,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
